import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { xosoService } from "@/services/xosoService";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const REGION = "MT";
const LABEL = "Miền Trung";
const DRAW_AT = "17:15";

const WEEKDAY_LONG = ["Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư", "Thứ năm", "Thứ sáu", "Thứ bảy"];

const PRIZE_KEYS = [
  ["specialPrize", "db"],
  ["prize1", "g1"],
  ["prize2", "g2"],
  ["prize3", "g3"],
  ["prize4", "g4"],
  ["prize5", "g5"],
  ["prize6", "g6"],
  ["prize7", "g7"],
  ["prize8", "g8"],
];

const PROVINCES = [
  "Phú Yên",
  "Thừa Thiên Huế",
  "Đắk Lắk",
  "Quảng Nam",
  "Đà Nẵng",
  "Khánh Hòa",
  "Quảng Bình",
  "Bình Định",
  "Quảng Trị",
  "Gia Lai",
  "Ninh Thuận",
  "Quảng Ngãi",
  "Đắk Nông",
  "Kon Tum",
];

const NAME_GROUPS = [
  ["hochiminh", "tphcm", "tphochiminh", "thanhphohochiminh"],
  ["thuathienhue", "hue"],
];

function todayKey() {
  return dayjs().tz(TZ).format("YYYY-MM-DD");
}

function yesterdayKey() {
  return dayjs().tz(TZ).subtract(1, "day").format("YYYY-MM-DD");
}

function parseClock(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

function nowMinutes() {
  const now = dayjs().tz(TZ);
  return now.hour() * 60 + now.minute();
}

function reachedDraw(scheduledTime) {
  const cutoff = parseClock(scheduledTime) ?? parseClock(DRAW_AT);
  if (cutoff == null) return false;
  return nowMinutes() >= cutoff;
}

function msUntilClock(clock) {
  const minutes = parseClock(clock);
  if (minutes == null) return null;
  const now = dayjs().tz(TZ);
  const target = now.hour(Math.floor(minutes / 60)).minute(minutes % 60).second(0).millisecond(0);
  const diff = target.diff(now);
  return diff > 0 ? diff : null;
}

function drawMeta(dateKey) {
  const parsed = dayjs.utc(dateKey);
  const [year, month, day] = dateKey.split("-");
  return {
    weekdayLong: WEEKDAY_LONG[parsed.day()],
    dash: `${day}-${month}-${year}`,
    slash: `${day}/${month}/${year}`,
  };
}

function foldName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function sameStation(a, b) {
  const left = foldName(a);
  const right = foldName(b);
  if (!left || !right) return false;
  if (left === right) return true;
  return NAME_GROUPS.some((group) => group.includes(left) && group.includes(right));
}

function displayName(apiName) {
  const hit = PROVINCES.find((name) => sameStation(name, apiName));
  return hit || apiName || "";
}

function toPrizes(prizes) {
  const out = {};
  PRIZE_KEYS.forEach(([from, to]) => {
    const list = prizes?.[from];
    out[to] = Array.isArray(list) ? list.map((value) => String(value)) : [];
  });
  return out;
}

function toStation(item) {
  const apiName = item?.province?.name || item?.provinceName || "";
  const key = item?.province?.code || item?.provinceCode || item?.id;
  if (!key) return null;
  return {
    key: String(key),
    name: displayName(apiName),
    prizes: toPrizes(item?.prizes),
  };
}

function toStations(rows) {
  const seen = new Set();
  const stations = [];
  (rows || []).forEach((item) => {
    const station = toStation(item);
    if (!station || seen.has(station.key)) return;
    seen.add(station.key);
    stations.push(station);
  });
  return stations;
}

function emptyLive() {
  return { region: REGION, schedule: [], drawDate: null, byCode: {} };
}

function parseLiveEvent(event) {
  try {
    const data = JSON.parse(event.data);
    if (!data || typeof data !== "object") return null;
    if (!data.kind) {
      if (Array.isArray(data.changes)) data.kind = "update";
      else if (Array.isArray(data.provinces) && data.drawDate) data.kind = "snapshot";
      else if (Array.isArray(data.provinces)) data.kind = "schedule";
    }
    return data;
  } catch {
    return null;
  }
}

function clonePrizes(prizes) {
  return { ...(prizes || {}) };
}

function reduceLive(state, event, today) {
  if (!event || (event.region && event.region !== state.region)) return state;

  if (event.kind === "schedule") {
    return { ...state, schedule: event.provinces || [] };
  }

  if (event.kind === "snapshot") {
    if (event.drawDate !== today) return state;
    const byCode = {};
    (event.provinces || []).forEach((item) => {
      const code = item.provinceCode || item.province?.code;
      if (!code) return;
      byCode[code] = {
        provinceCode: code,
        provinceName: item.provinceName || item.province?.name,
        prizes: clonePrizes(item.prizes),
      };
    });
    return { ...state, drawDate: event.drawDate, byCode };
  }

  if (event.kind === "update") {
    if (event.drawDate && event.drawDate !== today) return state;
    const byCode = { ...state.byCode };
    (event.changes || []).forEach((change) => {
      const code = change.provinceCode || change.province?.code;
      if (!code) return;
      const prev = byCode[code] || {
        provinceCode: code,
        provinceName: change.provinceName,
        prizes: {},
      };
      byCode[code] = {
        provinceCode: code,
        provinceName: change.provinceName || change.province?.name || prev.provinceName,
        prizes: { ...prev.prizes, ...(change.newPrizes || change.prizes || {}) },
      };
    });
    return { ...state, drawDate: event.drawDate || state.drawDate || today, byCode };
  }

  return state;
}

function orderedProvinces(state) {
  if (state.schedule.length) {
    return state.schedule.map((item) => {
      const code = item.provinceCode;
      return (
        state.byCode[code] || {
          provinceCode: code,
          provinceName: item.provinceName,
          prizes: {},
        }
      );
    });
  }
  return Object.values(state.byCode);
}

function hasAnyNumbers(provinces) {
  return (provinces || []).some((item) =>
    Object.values(item.prizes || {}).some((nums) => Array.isArray(nums) && nums.length)
  );
}

function mapLiveResults(provinces, drawDate) {
  return (provinces || []).map((item) => ({
    id: item.provinceCode,
    provinceId: item.provinceCode,
    region: REGION,
    drawDate,
    prizes: item.prizes || {},
    province: {
      code: item.provinceCode,
      name: item.provinceName,
      region: REGION,
    },
  }));
}

function rowsFromLive(state) {
  const today = todayKey();
  const columns = orderedProvinces(state);
  if (!columns.length) return null;
  const liveToday = state.drawDate === today;
  if (!liveToday && !hasAnyNumbers(columns)) return null;
  return mapLiveResults(columns, liveToday ? state.drawDate : today);
}

function fetchRows(params) {
  return xosoService.getXsmt(params);
}

export default function useXsmtV3Light() {
  const [tick, setTick] = useState(0);
  const [scheduledTime, setScheduledTime] = useState(null);
  const [rest, setRest] = useState(null);
  const [restDate, setRestDate] = useState(null);
  const [liveRows, setLiveRows] = useState(null);

  const opened = reachedDraw(scheduledTime);
  const dateKey = opened ? todayKey() : yesterdayKey();

  useEffect(() => {
    let cancelled = false;
    let timer;
    const poll = async () => {
      const data = await xosoService.getLiveStatus(REGION);
      if (cancelled) return;
      if (data?.scheduledTime) setScheduledTime(data.scheduledTime);
      const remaining = data?.secondsRemaining;
      const wait = remaining == null ? 20000 : remaining > 90 ? 15000 : 4000;
      timer = setTimeout(poll, wait);
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const wait = msUntilClock(scheduledTime || DRAW_AT);
    if (wait == null) return undefined;
    const timer = setTimeout(() => setTick((value) => value + 1), wait + 200);
    return () => clearTimeout(timer);
  }, [scheduledTime, tick]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const load = async () => {
      const rows = await fetchRows({ date: dateKey });
      if (cancelled) return;
      setRest(rows);
      setRestDate(dateKey);
      if (opened) timer = setTimeout(load, 15000);
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dateKey, opened]);

  useEffect(() => {
    if (!opened) {
      setLiveRows(null);
      return undefined;
    }

    let state = emptyLive();
    let lastEventId = "";
    let closed = false;
    let es;
    try {
      es = new EventSource(xosoService.getLiveStreamUrl(REGION));
    } catch {
      return undefined;
    }

    const handle = (event) => {
      try {
        const id = event.lastEventId || "";
        if (id && id === lastEventId) return;
        if (id) lastEventId = id;
        const data = parseLiveEvent(event);
        if (!data) return;
        const next = reduceLive(state, data, todayKey());
        if (next === state) return;
        state = next;
        if (closed) return;
        setLiveRows(rowsFromLive(state));
      } catch {
        return;
      }
    };

    es.addEventListener("schedule", handle);
    es.addEventListener("snapshot", handle);
    es.addEventListener("update", handle);
    es.onmessage = handle;

    return () => {
      closed = true;
      es.close();
    };
  }, [opened]);

  return useMemo(() => {
    const liveReady = Boolean(opened && liveRows?.length);
    const restReady = restDate === dateKey;
    const rows = liveReady ? liveRows : restReady ? rest || [] : [];
    return {
      label: LABEL,
      draw: drawMeta(dateKey),
      stations: toStations(rows),
      pending: !liveReady && !restReady,
    };
  }, [dateKey, liveRows, opened, rest, restDate]);
}
